/**
 * Server-Sent Events (SSE) Client
 * Handles POST-based SSE streams for chat
 *
 * Used for:
 * - /chat endpoint with streaming responses
 * - Real-time thinking steps
 * - Progressive response chunks
 *
 * On Capacitor native: uses the original (unpatched) window.fetch to bypass
 * CapacitorHttp, which intercepts fetch and buffers entire responses.
 * This lets SSE streaming work natively in the WKWebView.
 */
import { getOriginalFetch, isNative } from './capacitor'

/**
 * SSE Event
 */
export interface SseEvent<T = unknown> {
  type: string
  data: T
}

/**
 * Open a POST-based SSE stream
 * Returns an async generator that yields events
 *
 * @example
 * ```ts
 * for await (const event of postSse('/chat', { message: 'Hello' }, headers)) {
 *   if (event.type === 'thinking') {
 *     console.log('AI is thinking:', event.data)
 *   }
 * }
 * ```
 */
export async function* postSse<T = unknown>(
  url: string,
  body: unknown,
  headers: Record<string, string>,
  signal?: AbortSignal,
): AsyncGenerator<SseEvent<T>, void, unknown> {
  console.log('[SSE] Starting request:', { url, body, headers })

  // Use the original (unpatched) fetch on native to bypass CapacitorHttp
  // and get real SSE streaming via the WebView's native ReadableStream support.
  const sseFetch = isNative() ? getOriginalFetch() : fetch

  let response: Response
  let fallbackAttempted = false

  try {
    response = await sseFetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'text/event-stream',
        'Content-Type': 'application/json',
        ...headers,
      },
      credentials: isNative() ? 'omit' : 'include', // No cookies on native (uses Bearer token)
      body: JSON.stringify(body),
      signal,
    })
  } catch (error) {
    // If aborted, just return quietly
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('[SSE] Request aborted by user')
      return
    }
    // Network error (connection refused) - try x-agent directly
    if (url.includes('localhost:9080')) {
      const fallbackUrl = url.replace('localhost:9080', 'localhost:8300')
      console.warn('[SSE] Gateway failed, trying x-agent directly:', fallbackUrl)
      fallbackAttempted = true

      response = await sseFetch(fallbackUrl, {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          'Content-Type': 'application/json',
          ...headers,
        },
        credentials: isNative() ? 'omit' : 'include',
        body: JSON.stringify(body),
        signal,
      })
    } else {
      throw error
    }
  }

  console.log('[SSE] Response received:', {
    status: response.status,
    ok: response.ok,
    fallback: fallbackAttempted,
    headers: Object.fromEntries(response.headers.entries())
  })

  if (!response.ok || !response.body) {
    // Parse error body for structured info (e.g., 429 rate limit details)
    let errorMessage = `SSE request failed: ${response.status} ${response.statusText}`
    let resetAt: string | undefined
    let errorCode: string | undefined
    const retryAfterHeader = response.headers.get('Retry-After')
    const retryAfterSec = retryAfterHeader ? parseInt(retryAfterHeader, 10) : undefined
    try {
      const body = await response.json()
      if (body?.message) errorMessage = body.message
      else if (typeof body?.error === 'string') errorMessage = body.error
      if (typeof body?.resetAt === 'string') resetAt = body.resetAt
      if (typeof body?.error === 'string') errorCode = body.error
    } catch {
      // non-JSON body
    }
    const err = new Error(errorMessage) as Error & {
      status?: number
      resetAt?: string
      errorCode?: string
      retryAfterSec?: number
    }
    err.status = response.status
    if (resetAt) err.resetAt = resetAt
    if (errorCode) err.errorCode = errorCode
    if (retryAfterSec && !isNaN(retryAfterSec)) err.retryAfterSec = retryAfterSec
    throw err
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break

      // Append chunk to buffer
      buffer += decoder.decode(value, { stream: true })

      // Process complete messages (delimited by \n\n)
      let boundaryIndex
      while ((boundaryIndex = buffer.indexOf('\n\n')) !== -1) {
        const rawMessage = buffer.slice(0, boundaryIndex)
        buffer = buffer.slice(boundaryIndex + 2)

        // Parse SSE message format
        const lines = rawMessage.split('\n')
        let eventType = 'message'
        const dataLines: string[] = []

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            dataLines.push(line.slice(5).trim())
          }
        }

        // Combine multi-line data
        const dataString = dataLines.join('')

        // Keep-alive heartbeats: consume silently, don't propagate
        if (eventType === 'heartbeat') continue

        // Structured in-stream error frames: throw a typed Error so callers
        // get the same shape as pre-stream HTTP errors (status, resetAt,
        // errorCode). This branch owns the throw; upstream for-await loops
        // should let the thrown error propagate to their outer try/catch.
        if (eventType === 'error') {
          let payload: {
            message?: string
            status?: number
            resetAt?: string
            errorCode?: string
          } | null = null
          try {
            payload = JSON.parse(dataString)
          } catch {
            throw new Error(dataString || 'Chat error')
          }
          const err = new Error(payload?.message || 'Chat error') as Error & {
            status?: number
            resetAt?: string
            errorCode?: string
          }
          if (payload?.status) err.status = payload.status
          if (payload?.resetAt) err.resetAt = payload.resetAt
          if (payload?.errorCode) err.errorCode = payload.errorCode
          throw err
        }

        if (dataString) {
          try {
            const data = JSON.parse(dataString) as T
            console.log('[SSE] YIELDING event:', eventType, data)
            yield { type: eventType, data }
            console.log('[SSE] AFTER YIELD - control returned from consumer')
          } catch (error) {
            // Non-JSON data is ignored (could log if needed)
            console.warn('[SSE] Failed to parse event data:', dataString, error)
          }
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

